import * as THREE from 'three';

const EARTH_RADIUS = 2.05;

export class Earth {
  readonly group = new THREE.Group();
  private readonly surfaceGeometry = new THREE.SphereGeometry(EARTH_RADIUS, 64, 48);
  private readonly cloudGeometry = new THREE.SphereGeometry(EARTH_RADIUS * 1.018, 48, 32);
  private readonly atmosphereGeometry = new THREE.SphereGeometry(EARTH_RADIUS * 1.07, 48, 32);
  private readonly surfaceTexture = this.createSurfaceTexture();
  private readonly cloudTexture = this.createCloudTexture();
  private readonly surfaceMaterial = new THREE.MeshStandardMaterial({
    map: this.surfaceTexture,
    roughness: 0.84,
    metalness: 0,
  });
  private readonly cloudMaterial = new THREE.MeshStandardMaterial({
    map: this.cloudTexture,
    transparent: true,
    opacity: 0.62,
    depthWrite: false,
    roughness: 1,
  });
  private readonly atmosphereMaterial = new THREE.MeshBasicMaterial({
    color: '#67bce8',
    transparent: true,
    opacity: 0.12,
    side: THREE.BackSide,
    depthWrite: false,
  });
  private readonly surface = new THREE.Mesh(this.surfaceGeometry, this.surfaceMaterial);
  private readonly clouds = new THREE.Mesh(this.cloudGeometry, this.cloudMaterial);

  constructor() {
    const atmosphere = new THREE.Mesh(this.atmosphereGeometry, this.atmosphereMaterial);
    this.group.add(this.surface, this.clouds, atmosphere);
    this.group.rotation.z = -0.18;
  }

  update(delta: number, reducedMotion: boolean): void {
    const motion = reducedMotion ? 0.22 : 1;
    this.surface.rotation.y += delta * 0.055 * motion;
    this.clouds.rotation.y += delta * 0.072 * motion;
  }

  dispose(): void {
    this.surfaceGeometry.dispose();
    this.cloudGeometry.dispose();
    this.atmosphereGeometry.dispose();
    this.surfaceTexture.dispose();
    this.cloudTexture.dispose();
    this.surfaceMaterial.dispose();
    this.cloudMaterial.dispose();
    this.atmosphereMaterial.dispose();
  }

  private createSurfaceTexture(): THREE.CanvasTexture {
    const width = 512;
    const height = 256;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Could not create Earth texture.');
    const image = context.createImageData(width, height);
    const hash = (x: number, y: number): number => {
      let value = Math.imul(x, 374761393) + Math.imul(y, 668265263) + 0x2c9277b5;
      value = Math.imul(value ^ (value >>> 13), 1274126177);
      return ((value ^ (value >>> 16)) >>> 0) / 4294967295;
    };
    const noise = (x: number, y: number, scale: number): number => {
      const sx = x / scale;
      const sy = y / scale;
      const x0 = Math.floor(sx);
      const y0 = Math.floor(sy);
      const tx = THREE.MathUtils.smoothstep(sx - x0, 0, 1);
      const ty = THREE.MathUtils.smoothstep(sy - y0, 0, 1);
      const a = THREE.MathUtils.lerp(hash(x0, y0), hash(x0 + 1, y0), tx);
      const b = THREE.MathUtils.lerp(hash(x0, y0 + 1), hash(x0 + 1, y0 + 1), tx);
      return THREE.MathUtils.lerp(a, b, ty);
    };
    for (let y = 0; y < height; y += 1) {
      const latitude = Math.abs(y / (height - 1) * 2 - 1);
      for (let x = 0; x < width; x += 1) {
        const broad = noise(x, y, 54) * 0.58 + noise(x + 131, y - 47, 27) * 0.29 + noise(x, y, 12) * 0.13;
        const land = broad > 0.535 + latitude * 0.055 && latitude < 0.86;
        const polar = latitude > 0.82 + noise(x, y, 20) * 0.1;
        const offset = (y * width + x) * 4;
        if (polar) {
          image.data[offset] = 224;
          image.data[offset + 1] = 235;
          image.data[offset + 2] = 229;
        } else if (land) {
          const dry = THREE.MathUtils.clamp(latitude * 0.62 + noise(x + 9, y + 17, 18) * 0.55, 0, 1);
          image.data[offset] = Math.round(55 + dry * 70);
          image.data[offset + 1] = Math.round(116 + dry * 29);
          image.data[offset + 2] = Math.round(67 - dry * 22);
        } else {
          const water = noise(x + 73, y + 29, 22);
          image.data[offset] = Math.round(18 + water * 15);
          image.data[offset + 1] = Math.round(77 + water * 34);
          image.data[offset + 2] = Math.round(137 + water * 42);
        }
        image.data[offset + 3] = 255;
      }
    }
    context.putImageData(image, 0, 0);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    return texture;
  }

  private createCloudTexture(): THREE.CanvasTexture {
    const width = 256;
    const height = 128;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Could not create Earth cloud texture.');
    const image = context.createImageData(width, height);
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const wave = Math.sin(x * 0.082 + Math.sin(y * 0.11) * 2.3)
          + Math.sin(x * 0.031 - y * 0.17)
          + Math.cos(x * 0.15 + y * 0.074);
        const alpha = THREE.MathUtils.smoothstep(wave, 0.72, 2.25) * 178;
        const offset = (y * width + x) * 4;
        image.data[offset] = 244;
        image.data[offset + 1] = 248;
        image.data[offset + 2] = 247;
        image.data[offset + 3] = Math.round(alpha);
      }
    }
    context.putImageData(image, 0, 0);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    return texture;
  }
}
